/**
 * Phone-number handling for the fields that feed SMS notification.
 *
 * ── Why the browser validates this at all ──────────────────────────────────────────────────
 *
 * The User Management Lambda validates on write and is the authority — this does not replace it.
 * It exists because the failure it prevents is invisible: a number saved in the wrong shape does
 * not break anything an admin can see, it just means that one employee silently receives no
 * attendance SMS, possibly for months, while their record looks complete in the table.
 *
 * The rule is deliberately identical to normalizePhoneE164 in the user-management Lambda and in
 * the attendance-sync notifier. Three copies, because those are separately deployed packages
 * with no shared module between them, and a divergence would show up as a number the form
 * accepts and the backend rejects.
 */

/**
 * E.164: a leading +, a country code that cannot start with 0, and at most 15 digits.
 *
 * The lower bound is loose rather than tuned to India, so hiring someone in a country with
 * shorter numbers is not a silent delivery failure.
 */
export const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

/** Punctuation people type into a phone field that carries no information. */
const PUNCTUATION = /[\s()./-]/g;

/**
 * The storable E.164 form of what someone typed, or null when there is not one.
 *
 * An empty input returns '' — clearing the field is a legitimate edit, not a validation failure.
 * A bare "9876543210" returns null rather than being rescued with a +91: guessing a country code
 * would send one employee's notice to a stranger abroad the first time somebody is hired outside
 * India, and a rejected form is recoverable where a misdirected SMS is not.
 */
export function normalizePhoneE164(value: string | null | undefined): string | null {
  const stripped = String(value ?? '').replace(PUNCTUATION, '');
  if (stripped === '') return '';
  return E164_PATTERN.test(stripped) ? stripped : null;
}

/** Whether a value is already in storable E.164 form. */
export function isE164(value: string | null | undefined): boolean {
  return typeof value === 'string' && E164_PATTERN.test(value);
}
